<script setup lang="ts">
  import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/vue-query'

  import type { ProductOut } from '@/shared/api/intranet'
  import { getProductsShopProductsGet } from '@/shared/api/intranet'
  import SearchInput from '@/shared/ui/components/SearchInput.vue'

  import { useProfile } from '@/entities/profile'

  import { getTags } from '@/features/shop'

  import { useShopFiltersByQueries } from '@/views/shop'
  import { ActionsBar } from '@/views/shop/actionsBar'
  import { ShopAddMerchButton } from '@/views/shop/add-merch'
  import { ShopItem } from '@/views/shop/item'

  definePage({
    meta: { layout: 'baseNew' },
  })

  const { isProbationPassed } = storeToRefs(useProfile())

  const router = useRouter()
  if (!isProbationPassed.value) {
    router.push('/')
  }

  const { t } = useI18n()
  useHead({
    title: () => t('shop_heading'),
  })

  const queryClient = useQueryClient()

  const { isAdmin, isShopManager } = storeToRefs(useProfile())
  const allowedToEdit = computed(() => isShopManager.value || isAdmin.value)
  const { filters, debouncedSearch } = useShopFiltersByQueries()

  const { data, isPending, refetch, fetchNextPage, hasNextPage, isFetching } = useInfiniteQuery({
    queryKey: computed(() => ['shopList', filters.sort, filters.order, filters.query, filters.tag_ids]),
    queryFn: ({ signal, pageParam }) => {
      return getProductsShopProductsGet({
        query: {
          query: filters.query || undefined,
          page: pageParam,
          per_page: 20,
          sort: filters.sort ?? undefined,
          order: filters.order ?? undefined,
          tag_ids: filters.tag_ids ?? undefined,
          deleted: allowedToEdit.value ? false : undefined,
          all_locations: allowedToEdit.value,
        },
        signal,
      })
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      const totalPagesString = lastPage?.response?.headers.get('x-total-pages')
      const totalPagesNumber = totalPagesString && !Number.isNaN(+totalPagesString) ? +totalPagesString : 0
      if (!totalPagesNumber || totalPagesNumber <= lastPageParam) {
        return undefined
      }

      return lastPageParam + 1
    },
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  })

  const updateShopItems = () => {
    refetch()
    queryClient.invalidateQueries({ queryKey: ['tags-list'] })
  }

  const items = computed<ProductOut[]>(() => {
    return data.value?.pages.flatMap((pageItem) => pageItem.data?.products ?? []) || []
  })
  const totalItems = computed(() =>
    data.value?.pages.at(-1)?.response?.headers.get('x-total')
      ? Number(data.value?.pages.at(-1)?.response?.headers.get('x-total'))
      : 0,
  )
  const itemsLoadProgressPercentage = computed(() =>
    totalItems.value ? (items.value.length / totalItems.value) * 100 : 0,
  )

  const showItemsList = computed(() => !isPending.value && items.value.length > 0)
  const showNotFound = computed(() => !isPending.value && items.value.length === 0 && filters.query.length > 0)

  const isCategoriesOpen = ref(false)
  const isSearchOpen = ref(false)

  const isOverlayOpen = computed(() => isCategoriesOpen.value || isSearchOpen.value)

  const { data: tagsData } = useQuery({
    queryKey: ['tags-list'],
    queryFn: () => getTags(),
  })

  const tags = computed(() => {
    return tagsData?.value?.filter((tag) => tag.product_count > 0) || []
  })

  const selectedTagsValue = ref<number[] | undefined>()

  const handleSelectCategory = (catId: number) => {
    if (!selectedTagsValue.value?.length) {
      selectedTagsValue.value = [catId]
      return
    }
    if (selectedTagsValue.value?.includes(catId)) {
      selectedTagsValue.value = selectedTagsValue.value.filter((id) => id !== catId)
      return
    }
    selectedTagsValue.value = [...selectedTagsValue.value, catId]
  }

  const resetSelectedCategories = () => {
    selectedTagsValue.value = []
    filters.tag_ids = []
  }

  const applyCategories = (closeCallback: () => void) => {
    filters.tag_ids = selectedTagsValue.value
    closeCallback()
  }
  const handleSearch = (closeCallback: () => void) => {
    if (debouncedSearch.value === '' || debouncedSearch.value.length > 2) {
      filters.query = debouncedSearch.value
    }
    closeCallback()
  }

  const actionsContainer = useTemplateRef('container')

  onMounted(() => {
    if (filters.tag_ids) {
      selectedTagsValue.value = [...filters.tag_ids]
    }
  })

  const searchInputRef = useTemplateRef('searchInputRef')
  const categoriesPanelRef = useTemplateRef('categoriesPanelRef')
  const actionsBarRef = useTemplateRef('actionsBarRef')

  const handleSearchOpening = async (isOpen: boolean) => {
    if (!isOpen) {
      return
    }

    await nextTick()

    if (!searchInputRef.value) {
      return
    }

    searchInputRef.value.focusInput()

    const searchParent = searchInputRef.value.$el.parentElement

    const stop = onClickOutside(searchParent, () => {
      actionsBarRef.value?.closeSearch()
      stop()
    })

    onWatcherCleanup(stop)
  }

  const handleCategoriesOpening = async (isOpen: boolean) => {
    if (!isOpen) {
      return
    }

    await nextTick()

    if (!categoriesPanelRef.value) {
      return
    }

    const stop = onClickOutside(categoriesPanelRef.value, () => {
      actionsBarRef.value?.closeCategories()
      stop()
    })

    onWatcherCleanup(stop)
  }

  watch(isSearchOpen, handleSearchOpening)
  watch(isCategoriesOpen, handleCategoriesOpening)
</script>

<template>
  <div
    class="wide-responsive flex h-full flex-col flex-nowrap! items-start justify-start"
    :aria-label="$t('shop_aria_main')"
  >
    <ActionsBar
      ref="actionsBarRef"
      v-model:search="filters.query"
      v-model:categories="filters.tag_ids"
      v-model:sort="filters.sort"
      v-model:order="filters.order"
      class="w-full p-6"
      :categories-container="actionsContainer"
      :search-container="actionsContainer"
      @reset-categories="resetSelectedCategories"
      @show-categories="isCategoriesOpen = $event"
      @show-search="isSearchOpen = $event"
    >
      <template #categories="{ close }">
        <div
          v-if="isCategoriesOpen"
          ref="categoriesPanelRef"
          role="dialog"
          aria-modal="false"
          :aria-label="$t('shop_aria_categories_filter')"
          class="bg-surface-bg absolute top-0 right-0 -left-4 z-2 border-t p-6"
        >
          <ul
            class="flex list-none flex-wrap gap-4 px-6"
            role="list"
          >
            <li
              v-for="tag in tags"
              :key="tag.id"
            >
              <NTag
                class="cursor-pointer"
                size="large"
                role="button"
                tabindex="0"
                :aria-pressed="selectedTagsValue?.includes(tag.id) ? 'true' : 'false'"
                :aria-label="$t('shop_aria_category_filter', { name: tag.name })"
                :type="selectedTagsValue?.includes(tag.id) ? 'primary' : undefined"
                @click="handleSelectCategory(tag.id)"
                @keydown.enter.space.prevent="handleSelectCategory(tag.id)"
              >
                {{ tag.name }}
              </NTag>
            </li>
          </ul>
          <div class="mt-4 px-6">
            <NButton
              type="primary"
              secondary
              strong
              @click="applyCategories(close)"
            >
              {{ $t('base_apply') }}
            </NButton>
          </div>
        </div>
      </template>
      <template #search="{ close }">
        <div
          v-if="isSearchOpen"
          role="search"
          :aria-label="$t('shop_aria_search_panel')"
          class="bg-surface-bg absolute top-0 right-0 -left-4 z-2 flex justify-center border-t p-6"
        >
          <SearchInput
            ref="searchInputRef"
            :model-value="debouncedSearch"
            :max-length="200"
            class="max-w-175!"
            :placeholder="$t('shop_search-placeholder')"
            clearable
            @update:model-value="debouncedSearch = $event"
            @search="handleSearch(close)"
          >
            <template #prefix>
              <i
                class="fa-regular fa-magnifying-glass text-icon-hover"
                aria-hidden="true"
              />
            </template>
          </SearchInput>
        </div>
      </template>
    </ActionsBar>

    <div
      ref="container"
      class="relative w-full"
    >
      <div
        :class="{ 'pointer-events-none opacity-50': isOverlayOpen }"
        :aria-hidden="isOverlayOpen ? 'true' : undefined"
      >
        <section
          v-if="isPending || showItemsList"
          class="flex min-h-155 w-full flex-col p-2"
          :aria-label="$t('shop_aria_products_list')"
          :aria-busy="isPending ? 'true' : 'false'"
        >
          <div
            class="max-mobile:grid-cols-1 mx-auto my-0 grid w-full grid-cols-4 gap-2"
            role="list"
          >
            <template v-if="isPending">
              <NSkeleton
                v-for="idx in 8"
                :key="idx"
                height="300px"
                width="100%"
                :sharp="false"
                class="rounded-2xl!"
              />
            </template>
            <template v-if="showItemsList">
              <ShopItem
                v-for="item in items"
                :key="item.id"
                role="listitem"
                :item="item"
                @update-list="updateShopItems"
              />
            </template>
          </div>
          <div
            v-if="!isPending"
            class="mt-auto flex justify-center pt-4 pb-6"
          >
            <div class="max-w-50 text-center">
              <div class="body-xs">{{ $t('shop_list-showed', { items: items.length, total: totalItems }) }}</div>
              <NProgress
                class="my-2"
                :percentage="itemsLoadProgressPercentage"
                type="line"
                :show-indicator="false"
                :aria-label="$t('shop_aria_progress_loaded')"
                :aria-valuenow="items.length"
                :aria-valuemin="0"
                :aria-valuemax="totalItems"
                :theme-overrides="{
                  railColor: 'var(--color-silver-100)',
                  fillColor: 'var(--color-silver-400)',
                  railHeight: '1px',
                }"
              />
              <div>
                <NSpin
                  v-if="!isPending && isFetching"
                  show
                  size="large"
                />
                <NButton
                  v-if="!isFetching && hasNextPage"
                  quaternary
                  type="primary"
                  size="tiny"
                  strong
                  :aria-label="$t('shop_aria_loading_more')"
                  @click="() => fetchNextPage()"
                >
                  {{ $t('base_show-more') }}
                </NButton>
              </div>
            </div>
          </div>
        </section>
        <section
          v-if="showNotFound"
          class="flex h-full w-full flex-col flex-nowrap! items-center justify-start px-1.5 py-0"
          :aria-label="$t('shop_item_not_found')"
        >
          <div class="flex w-full flex-col items-center justify-center pt-8">
            <img
              src="@/assets/img/shop-item-not-found.png"
              alt=""
              role="presentation"
            />
          </div>
          <div class="body-lg mt-6">
            {{ $t('shop_item_not_found') }}
          </div>
        </section>
      </div>
    </div>

    <ShopAddMerchButton />
  </div>
</template>
